import React from 'react';
import { withRouter } from 'react-router-dom';
import { Card, CardBody, CardFooter, CardHeader, Container } from 'reactstrap';
import './DailyTimesheetForm.scss';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { CircularProgress } from '@mui/material';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { AppNavPaths } from '../AppNavPaths';
import DailyTimesheetHeader from './DailyTimesheetHeader';
import DailyTimesheetDetails from './DailyTimesheetDetails';
import DailyTimesheetActionBar from './DailyTimesheetActionBar';
import { toasty } from '../common/forms/FormElements';
import { TimesheetStatus } from '../timesheet/Timesheet';

class DailyTimesheetForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            saving: false,
            anyChanges: false,
            dailyTimesheet: {},
            editModeEnabled: false,
        };
    }

    componentDidMount() {
        this.populateState(true);
    }

    async populateState() {
        const params = this.props.match.params;

        const { date, id } = params;

        const [dailyTimesheet, userProfile, timesheetStatuses, contracts] =
            await Promise.all([
                !id
                    ? util.fetch.js(ApiRoutes.dailyTimesheets.getFor(date))
                    : util.fetch.js(ApiRoutes.dailyTimesheets.get(id)),
                getTenantUserProfile(),
                util.fetch.js(ApiRoutes.typeAheads.timesheetStatuses()),
                util.fetch.js(ApiRoutes.contract.getSelectable()),
            ]);

        const { userPermissions } = userProfile;

        const isAdmin =
            userPermissions &&
            userPermissions.includes('dailytimesheet.accept');

        const isApprover =
            userPermissions &&
            userPermissions.includes('dailytimesheet.approve');

        this.setState({
            id,
            isAdmin,
            isApprover,
            anyChanges: id == 0,
            dailyTimesheet,
            loading: false,
            timesheetStatuses,
            contracts,
        });
    }

    onSaveClick = async () => {
        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.dailyTimesheets.save(),
            dailyTimesheet
        );

        if (saveResponse) {
            this.setState({ dailyTimesheet: saveResponse, saving: false });

            toasty.success(
                'Timesheet Saved',
                'Your timesheet has been saved, but not submitted.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onSubmitClick = async () => {
        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const submitResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.dailyTimesheets.submit(),
            dailyTimesheet
        );

        if (submitResponse) {
            this.setState({ dailyTimesheet: submitResponse, saving: false });

            toasty.success(
                'Timesheet Submitted',
                'Your timesheet has been submitted.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onAcceptClick = async () => {
        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.dailyTimesheetManagement.accept(dailyTimesheet.id),
            null
        );

        if (saveResponse) {
            this.setState({ dailyTimesheet: saveResponse, saving: false });
            toasty.success(
                'Timesheet Approved',
                'The timesheet has been approved.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onRejectClick = async (rejectionNotes) => {
        if (!rejectionNotes) {
            return;
        }

        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const submitResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.dailyTimesheetManagement.reject(),
            {
                dailyTimesheetId: dailyTimesheet.id,
                rejectionNotes,
            }
        );

        if (submitResponse) {
            this.setState({ dailyTimesheet: submitResponse, saving: false });
            toasty.success(
                'Timesheet Rejected',
                'The timesheet has been rejected.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onRecallRequested = async () => {
        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.dailyTimesheets.recall(dailyTimesheet.id),
            null
        );

        if (saveResponse) {
            this.setState({ dailyTimesheet: saveResponse, saving: false });
            toasty.success(
                'Timesheet Request Recalled',
                'Your timesheet has been recalled for further editing.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onBackClick = () => {
        const { dailyTimesheet } = this.state;

        if (dailyTimesheet.createdByCurrentUser || !dailyTimesheet.id) {
            this.props.history.push(AppNavPaths.AssignmentCalendar);
        } else {
            this.props.history.push(AppNavPaths.TimesheetManagement);
        }
    };

    onDeleteClick = async () => {
        this.setState({ saving: true });

        const { dailyTimesheet } = this.state;

        const deleteResponse = await util.fetch.delete(
            ApiRoutes.dailyTimesheets.delete(dailyTimesheet.id)
        );

        if (deleteResponse) {
            toasty.success(
                'Timesheet Deleted',
                'Your timesheet has been deleted.'
            );

            this.onBackClick();

            return;
        }

        toasty.error(
            'Delete Failed',
            'We were unable to delete this timesheet.'
        );

        this.setState({ saving: false });
    };

    onDailyTimesheetMutated = (dailyTimesheet) => {
        this.setState({ dailyTimesheet, anyChanges: true });
    };

    getIsLockedDown = () => {
        const { dailyTimesheet, isAdmin, isApprover } = this.state;

        if (dailyTimesheet.createdByCurrentUser) {
            return (
                dailyTimesheet.timesheetStatusId != TimesheetStatus.New &&
                dailyTimesheet.timesheetStatusId != TimesheetStatus.Rejected
            );
        }

        if (!isAdmin) {
            return true;
        }

        if (
            dailyTimesheet.timesheetStatusId >= TimesheetStatus.Exported ||
            (!isApprover &&
                dailyTimesheet.timesheetStatusId >= TimesheetStatus.Validated)
        ) {
            return true;
        }
    };

    getIsReadOnly = () => {
        if (this.getIsLockedDown()) {
            return true;
        }

        const { dailyTimesheet, isAdmin, editModeEnabled } = this.state;

        if (dailyTimesheet.createdByCurrentUser) {
            return (
                dailyTimesheet.timesheetStatusId != TimesheetStatus.New &&
                dailyTimesheet.timesheetStatusId != TimesheetStatus.Rejected
            );
        }

        return (
            !dailyTimesheet.createdByCurrentUser && isAdmin && !editModeEnabled
        );
    };

    render() {
        const { loading } = this.state;

        const readOnly = this.getIsReadOnly();
        const lockedDown = this.getIsLockedDown();

        if (loading) {
            return (
                <CircularProgress color="secondary" variant="indeterminate" />
            );
        }

        return (
            <Container
                className="daily-timesheet-container p-3 m-3 ml-auto mr-auto"
                fluid={true}
            >
                <Card>
                    <CardHeader>
                        <DailyTimesheetHeader
                            {...this.state}
                            readOnly={readOnly}
                            lockedDown={lockedDown}
                            onDeleteClick={this.onDeleteClick}
                        />
                    </CardHeader>
                    <CardBody>
                        <DailyTimesheetDetails
                            {...this.state}
                            onDailyTimesheetMutated={
                                this.onDailyTimesheetMutated
                            }
                            readOnly={readOnly}
                            lockedDown={lockedDown}
                            onRecallRequested={this.onRecallRequested}
                        />
                    </CardBody>
                    <CardFooter>
                        <DailyTimesheetActionBar
                            {...this.state}
                            onSaveClick={this.onSaveClick}
                            onSubmitClick={this.onSubmitClick}
                            onBackClick={this.onBackClick}
                            onRejectClick={this.onRejectClick}
                            onAcceptClick={this.onAcceptClick}
                            readOnly={readOnly}
                            lockedDown={lockedDown}
                        />
                    </CardFooter>
                </Card>
            </Container>
        );
    }
}

export default withRouter(DailyTimesheetForm);
